import {FULFILLED} from 'redux-promise-middleware';
import {takeEvery, call, select, all} from 'redux-saga/effects';
import {pathOr} from 'ramda';
import {
    pushDataLayer,
    createEECCheckoutStepEvent,
    createEECPurchaseEvent,
} from '@optimaxdev/analytics/mobile';

import {APP} from 'constants/actions';
import {isInsuranceSuccessPage as isInsuranceSuccessPageSelector} from 'selectors/app/app';
import {getOrderId as getMainOrderId} from 'pages/checkout/store/selectors/orderData/orderData';
import {
    getOrderId,
    getInsurancePeriod,
    getSelectedProducts,
    getSelectedInsurance,
    getTotalPrice,
} from 'pages/postPurchaseInsurance/store/order/selectors';
import {getCustomerCurrency} from 'selectors/customer/customer';
import {getTotalsState} from 'pages/postPurchaseInsurance/store/totals/selectors';
import {getStatePayment} from 'pages/postPurchaseInsurance/store/payment/selectors';
import {getOrder} from 'pages/postPurchaseInsurance/store/order/actions';
import {changeStep} from 'pages/postPurchaseInsurance/store/step/actions';
import {getCurrentStep} from 'pages/postPurchaseInsurance/store/step/selectors';
import {DATA_DONOR, STEP, STEPS} from 'pages/postPurchaseInsurance/constants';
import {PostPurchaseOrderType} from 'pages/postPurchaseInsurance/sagas/EEC/type';
import {setOrderId} from 'pages/postPurchaseInsurance/store/placeOrder/actions';
import {getLastOrderData} from 'sagas/getLastOrderData';

import {
    prepareProductDataForPostPurchase,
    prepareInsuranceDataForPostPurchase,
} from './preparePostInsuranceData';

/**
 * Collects and returns
 * product/insurance impression`s data about
 *
 * @param {string} impressionsDonor - item the data about will be collected
 * @return {PostPurchaseImpressionsType} impressions data
 */
export function* getImpressionsData(impressionsDonor: string) {
    const [selectedPeriod, products]: [
        ReturnType<typeof getInsurancePeriod>,
        ReturnType<typeof getSelectedProducts>,
    ] = yield all([select(getInsurancePeriod), select(getSelectedProducts)]);

    if (impressionsDonor === DATA_DONOR.PRODUCT) {
        return yield call(prepareProductDataForPostPurchase, products, selectedPeriod);
    }

    const selectedInsurance = yield call(getSelectedInsurance, products[0], selectedPeriod);
    const totalPrice: ReturnType<typeof getTotalPrice> = yield select(getTotalPrice);

    return yield call(
        prepareInsuranceDataForPostPurchase,
        [selectedInsurance],
        products.length,
        totalPrice,
    );
}
/**
 * Get ecommerce data
 * 
 * @param {string} impressionsDonor - item the data about will be collected
 * @returns {{
        ecommerce: {
          currencyCode: string,
          impressions: PostPurchaseImpressionsType,
      },
      originalOrderNumber: string,
* }} order data for eec analytics
*/
export function* getEcommerceData(impressionsDonor = DATA_DONOR.INSURANCE) {
    const [originalOrderNumber, currency]: [
        ReturnType<typeof getOrderId>,
        ReturnType<typeof getCustomerCurrency>,
    ] = yield all([select(getOrderId), select(getCustomerCurrency)]);

    const impressions = yield call(getImpressionsData, impressionsDonor);

    return {
        ecommerce: {
            currencyCode: currency.code,
            impressions,
        },
        originalOrderNumber,
    };
}

/**
 * Preparing data for sending an event for the checkout step
 * @param {number} step - Payment step
 * @returns {{
    ecommerce: {impressions: Array<PostPurchaseProductType>},
    originalOrderNumber: string,
}} order data for e-commerce analytics
*/
export function* prepareDataForPostPurchaseStep(step: number) {
    const {
        ecommerce: {impressions: products},
        originalOrderNumber,
    } = yield call(getEcommerceData);
    return {
        ...createEECCheckoutStepEvent({step, products}),
        originalOrderNumber,
    };
}

/**
 * Send an EEC event for purchased products
 */
export function* sendEECPostPurchase() {
    const ecommerceData = yield call(getEcommerceData, DATA_DONOR.PRODUCT);
    yield call(pushDataLayer, {
        ...ecommerceData,
        event: 'EEC_PostPurchase_impressions',
    });
}

/**
 * Send an EEC event for payment step
 */
export function* sendEECPostPurchasePaymentStep() {
    const currentStep = yield select(getCurrentStep);
    if (currentStep !== STEP.PAYMENT) return;
    const data = yield call(prepareDataForPostPurchaseStep, STEPS.PAYMENT);
    yield call(pushDataLayer, data);
}

/**
 * Send an EEC event for complete step
 */
export function* sendEECPostPurchaseCompletedStep() {
    const data = yield call(prepareDataForPostPurchaseStep, STEPS.COMPLETED);
    yield call(pushDataLayer, data);
}

/**
 * Send an EEC event for completed purchase
 * @param {PostPurchaseOrderType} order - Order details
 */
export function* sendEECPostPurchasePaymentCompleted({
    ecommerce: {impressions: products},
    originalOrderNumber,
    taxTotal,
    subTotal,
}: PostPurchaseOrderType) {
    const {id} = yield select(getMainOrderId);
    const paymentDetails = yield call(getLastOrderData);
    const sku = pathOr('', ['items', 0, 'sku'], paymentDetails);

    yield call(
        pushDataLayer,
        createEECPurchaseEvent({
            actionField: {
                action: 'purchase',
                id,
                revenue: String(subTotal),
                tax: taxTotal.toFixed(4),
            },
            products: products.map(product => ({
                ...product,
                id: sku || product.id,
            })),
            originalOrderNumber,
        }),
    );
}

/**
 * Checking the order for the type of purchase
 */
export function* checkPurchase() {
    const isInsuranceSuccessPage = yield select(isInsuranceSuccessPageSelector);
    if (!isInsuranceSuccessPage) return;

    const total = yield select(getTotalsState);
    const {activePayment: payment} = yield select(getStatePayment);
    const ecommerceData = yield call(getEcommerceData);

    const data = {...ecommerceData, ...total, payment};

    yield call(sendEECPostPurchasePaymentCompleted, data);
}

/**
 * Root EEC saga
 */
export function* rootEECPostPurchase() {
    yield takeEvery(`${getOrder}_${FULFILLED}`, sendEECPostPurchase);
    yield takeEvery(setOrderId, sendEECPostPurchaseCompletedStep);
    yield takeEvery(changeStep, sendEECPostPurchasePaymentStep);
    yield takeEvery(APP.SAVE_LOCATION, checkPurchase);
}
